/* currently skipped in flow */
import React, { useEffect, useState, useRef } from "react";
import WeightEstimation from "components/weight-estimation";
import egift_card from "assets/egift_card.svg";
import egift_cardWEBP from "assets/egift_cardWEBP.webp";
import StoreFormContainer from "components/store-form-container";
import { calculateEstimate } from "lib/calculateEstimate";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getQueryParams } from "lib/utils";
import { useRestartIfAbsent } from "hooks/useRestartIfAbsent";
import { Trans, useTranslation } from "react-i18next";

import { TAKEBACK_DISBURSEMENT_NAME } from "consts/query-params";
import { useTakebackById } from "api/use-takebacks";
import Checklist from "components/checklist";
import InputValidation from "components/input-validation";
import TermsText from "components/terms-text";
import { useAddStatus } from "api/tes/useAddStatus";
import { useUpdateTakeback } from "api/tes/useUpdateTakeback";
import {
  createVerificationStatus,
  createVerificationData,
} from "lib/verification-utils";

export default function StoreTerms() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [termsChecked, setTermsChecked] = useState(false);
  const [othersChecked, setOthersChecked] = useState(false);
  const [warning, setWarning] = useState(false);
  const [isBottom, setIsBottom] = useState(false);

  // translation
  const { t } = useTranslation();
  const unit = t("scale.weight");

  const location = useLocation();
  const queryParams = getQueryParams(location.search);

  const previouslySetWeight = queryParams.w;

  const estimate = calculateEstimate(previouslySetWeight);

  useRestartIfAbsent(
    queryParams,
    ["w", TAKEBACK_DISBURSEMENT_NAME],
    "/store/trade-in"
  );

  const checklistItems1 = [
    {
      name: "item1",
      label: (
        <span>
          I accept and{" "}
          <strong>have read all of the Terms and Conditions above</strong>
        </span>
      ),
    },
  ];

  const checklistItems2 = [
    { name: "item2", label: t("trade-in.terms.check2") },
    { name: "item3", label: t("trade-in.terms.check3") },
  ];

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setIsBottom(true);
      setWarning(false);
    }
  };

  const takeback = useTakebackById({ id: queryParams.id });

  const updatedParams = { ...queryParams };

  const { mutateAsync: addStatus } = useAddStatus();
  const { mutateAsync: updateTakeback } = useUpdateTakeback();

  const handleSubmit = async () => {
    const associateUser = JSON.parse(localStorage.getItem("user") || "{}");

    try {
      // Update takeback data with verification information
      const verificationData = createVerificationData("in-store", {
        weight: queryParams.w,
        paymentType: updatedParams[TAKEBACK_DISBURSEMENT_NAME],
        associateEmail: associateUser.email,
        acceptedTerms: true,
      });

      const updatedData = {
        ...takeback?.rawTakeback?.data,
        ...verificationData,
      };

      await updateTakeback({
        id: queryParams.id,
        input: {
          data: updatedData,
        },
      });

      // Add verification status similar to dropoff flow
      const verificationStatus = createVerificationStatus(
        "in-store",
        associateUser.email
      );
      await addStatus({
        id: queryParams.id,
        input: verificationStatus,
      });

      const searchParams = new URLSearchParams(updatedParams).toString();
      navigate(`${t("urls.store_confirmation")}?${searchParams}`);
    } catch (error) {
      console.error("Failed to update takeback with verification:", error);
      alert("Please try again");
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <StoreFormContainer
      onSubmit={handleSubmit}
      submitLabel="Confirm Trade-In"
      submitDisabled={!othersChecked || !termsChecked}
      secondaryLabel="Back"
      secondaryHref={t("urls.store_payout_confirm")}
      secondaryParams={updatedParams}
    >
      <div className="flex flex-col flex-grow py-4">
        <div className="flex flex-col mr-auto">
          <h1 className="text-6xl">
            Hey, {takeback ? takeback.consumer.firstName : "Seller"}
          </h1>
          <div className="text-tertiary text-2xl">
            Please review and agree to the Terms and Conditions.
          </div>
        </div>
        <div className="text-2xl">
          <WeightEstimation
            title="YOUR SELECTION"
            description={`LEGO® e-Gift Card. Based on ${previouslySetWeight} ${unit}`}
            singleValue={estimate.gift}
            className=""
            image={egift_card}
            imgSource={egift_cardWEBP}
            locale="en-US"
            animate
          />
        </div>
        <div className="mt-xxx-large px-auto">
          <div className="flex flex-col">
            <label className="mb-regular text-2xl font-semibold">
              Terms and Conditions
            </label>
            <div
              ref={containerRef}
              className="overflow-y-scroll h-[600px] w-full border border-gray-300 p-4 mb-6 rounded-xl"
            >
              <TermsText scroll={false} />
            </div>
            {warning && (
              <InputValidation errorMessage={t("trade-in.terms.warning")} />
            )}
            <div
              onClick={
                !isBottom ? () => setWarning(true) : () => setWarning(false)
              }
            >
              <Checklist
                checklistItems={checklistItems1}
                setAllChecked={setTermsChecked}
                className="mt-large"
                disabled={!isBottom}
              />
            </div>
            <Checklist
              checklistItems={checklistItems2}
              setAllChecked={setOthersChecked}
              className="mt-8"
            />
            <div className="mt-8">
              <Trans
                i18nKey="trade-in.terms.add1"
                components={{
                  1: (
                    <Link
                      to={t("urls.terms_conditions")}
                      className="text-blue"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </StoreFormContainer>
  );
}
